import type { SorConfigurationParamsSorAuthMode } from '~/server_shared/openapi/data-contracts';

export default {
  tabs: {
    configuration: 'Configuration',
    issues: 'Issues',
  },
  issueStatuses: {
    needs_attention: 'Needs attention',
    pending: 'Pending',
    remote_done: 'Done at the SoR',
    completed: 'Completed',
    failed: 'Failed',
  },
  issues: {
    status: 'Status',
    allStatuses: 'All statuses',
    operation: 'Operation',
    claimNumber: 'Claim number',
    from: 'From',
    to: 'To',
    clearFilters: 'Clear all filters',
    reconcileNow: 'Reconcile now',
    reconciled: (completed: number, failed: number, retrying: number, needsAttention: number) =>
      `Reconciled: ${completed} completed, ${failed} failed, ${retrying} still retried, ${needsAttention} needing attention.`,
    unreachable: 'The System of Record could not be reached; the attempts left wait for the next pass.',
    truncated: 'The pass ran out of time; the attempts left wait for the next pass.',
    errored: (errored: number) =>
      `${errored} ${errored === 1 ? 'attempt' : 'attempts'} could not be checked; the next pass retries them.`,
    claim: 'Claim',
    requestedBy: 'Requested by',
    started: 'Started',
    sorLatency: 'SoR latency',
    // Only the checks that failed count: a check that settles the attempt ends it instead.
    tries: 'Failed checks',
    triesValue: (tries: number, maxTries: number) => `${tries} / ${maxTries}`,
    none: '—',
    noIssues: 'No attempts match these filters.',
    refresh: 'Refresh attempts',
    truncatedResults: (count: number) => `Showing the first ${count} attempts. Refine the filters to see the rest.`,
  },
  issueDetails: {
    title: (attemptId: number) => `System of Record attempt ${attemptId}`,
    status: 'Status',
    commitStatus: 'Commit status',
    operation: 'Operation',
    entity: 'Entity',
    entityValue: (entityType: string, entityId: number) => `${entityType} · ${entityId}`,
    apiIdentifier: 'API identifier',
    claim: 'Claim',
    requestedBy: 'Requested by',
    resolvedBy: 'Resolved by',
    replayedAs: 'Replayed as',
    resolutionNote: 'Resolution note',
    idempotencyKey: 'Idempotency key',
    sorReference: 'SoR reference',
    started: 'Started',
    sent: 'Sent',
    responded: 'Responded',
    sorLatency: 'SoR latency',
    error: 'Error',
    // A completed attempt keeps the error it met on its way, such as the timeout that left it for the reconciler.
    errorBeforeConfirmed: 'Error before it was confirmed',
    sentPayload: 'Request payload',
    notSent: 'No payload was recorded for the SoR.',
    sorResponse: 'SoR response',
    noSorResponse: 'The SoR has not accepted this change.',
    reconciliationHistory: 'Reconciliation history',
    tries: 'Failed checks',
    lastReconciled: 'Last checked',
    close: 'Close',
  },
  resolve: {
    title: 'Resolve',
    completedAtSor: 'Completed at the SoR',
    notAtSor: 'Not at the SoR',
    sorReference: 'SoR reference',
    replayAs: 'Replay as',
    replayAsHelper: 'The change is made in CMS as this user, with their authority.',
    requester: (name: string) => `${name} (requester)`,
    note: 'Note',
    required: 'Required',
    submit: 'Resolve',
    pickAnotherUser: 'Pick another user under "Replay as" who can make this change, then resolve again.',
  },
  systemOfRecord: 'System of Record',
  enableSystemOfRecord: 'Enable System of Record',
  notConfigured: 'No System of Record is configured. Five Sigma sets it up for your organization.',
  userTokenTestedByCustomer:
    "The connection is tested with the testing user's own SSO token, so a user of the organization signed in through SSO must test it.",
  editConfiguration: 'Edit configuration',
  enabled: 'Enabled',
  disabled: 'Disabled',
  displayName: 'Display name',
  apiBaseUrl: 'API base URL',
  apiToken: 'API token',
  authentication: 'Authentication',
  // Keyed on the generated enum's values, so the copy of it in every schema indexes it.
  authModes: {
    static_token: 'Static API token',
    user_sso_token: "The user's SSO token",
  } satisfies Record<`${SorConfigurationParamsSorAuthMode}`, string>,
  ssoTokenScope: 'SSO token scope',
  extraHeader: 'Additional header',
  offlineAccessWarning:
    "The organization's SSO does not include offline_access, so its users have no SSO token for the System of Record. Before adding offline_access to the SSO scope, confirm the customer's admin consent for offline_access and the System of Record scope: adding it without that consent breaks the organization's SSO login.",
  timeouts: 'Timeouts',
  timeoutsValue: (connectSeconds: number, readSeconds: number, maxTries: number) =>
    `connect ${connectSeconds}s · read ${readSeconds}s · max reconcile tries ${maxTries}`,
  status: 'Status',
  notTested: 'Not tested',
  connected: 'Connected',
  connectionFailed: 'Connection failed',
  latency: (latencyMs: number) => `${latencyMs} ms`,
  connectionError: (errorCode: string, errorMessage: string) => `${errorCode}: ${errorMessage}`,
  testConnection: 'Test connection',
  operationsCommittedAtSor: 'Operations committed at the SoR',
  noOperations: 'No operation can be committed at a System of Record yet.',
  openAttemptCounts: (openAttempts: number, needsAttention: number) =>
    `Open attempts: ${openAttempts} · Needs attention: ${needsAttention}`,
  issuesLink: 'Issues',
  disableWarning: {
    title: 'Disable System of Record?',
    content: (openAttempts: number, needsAttention: number) =>
      `${openAttempts} ${openAttempts === 1 ? 'attempt is' : 'attempts are'} still open (${needsAttention} needing attention). While System of Record is disabled, open attempts are not retried and go stale.`,
    confirm: 'Disable',
  },
  dialog: {
    createTitle: 'Set up System of Record',
    editTitle: 'Edit System of Record configuration',
    displayName: 'Display name',
    apiBaseUrl: 'API base URL',
    apiToken: 'API token',
    showApiToken: 'Show API token',
    hideApiToken: 'Hide API token',
    authentication: 'Authentication',
    ssoTokenScope: 'SSO token scope',
    sendExtraHeader: 'Send an additional header',
    extraHeaderName: 'Header name',
    extraHeaderValue: 'Header value',
    showExtraHeaderValue: 'Show header value',
    hideExtraHeaderValue: 'Hide header value',
    invalidHeaderName: 'Not a valid HTTP header name',
    reservedHeaderName: 'CMS sets this header itself',
    connectTimeout: 'Connect timeout (s)',
    readTimeout: 'Read timeout (s)',
    maxTries: 'Max reconcile tries',
    operations: 'Operations committed at the SoR',
    saveNote: 'Saving runs a connection test; the integration cannot be enabled while the test fails.',
    save: 'Save',
    required: 'Required',
    timeoutRange: 'Between 1 and 30 seconds',
    maxTriesMinimum: 'At least 1',
  },
} as const;
